import { ChangeEvent, FormEvent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { SelectChangeEvent } from '@mui/material/Select';
import { useParams, useSearchParams } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';

import { CustomAlert, CustomButton, CustomInput, CustomSelect } from '../../../../../shared/uiComponents';
import { SelectDataProperties } from '../../../../../shared/uiComponents/Dropdown';
import { AdminTypes } from '../../../../../components/Menu';
import { errorNormalizer } from '../../../../../shared/Helpers/functions';

import { DispatchProperties, useSelector } from '../../../../../redux/store';
import {
	getBehaviorCategories,
	getBehaviorTypes,
	getBehaviors,
	saveBehavior,
} from '../../../../../redux/State/clientSlice/behaviorReductionSlice';
import { SaveClientBehaviorsProperties } from '../../../../../redux/API/ClientAPIHelpers/behaviorReductionProperties';
import { useScreenWidth } from '../../../../../shared/Helpers/hooks';
import { ELC_FUNDER_ID } from '../../../../../redux/API/constants';
import { Plus } from '../../../../../shared/Assets/svgIcons';
import { PlusIcon } from '../../../../../shared/Assets/svg';
import { BACKGROUND } from '../../../../../shared/Helpers/colors';

interface AddBehaviorProps {
	funderId?: string | null;
	mobileButton?: boolean;
}

const AddBehaviorContent = ({ funderId, onClose }: { funderId?: string | null; onClose: () => void }) => {
	const dispatch = useDispatch<DispatchProperties>();
	const { clientId } = useParams();
	const [params] = useSearchParams();

	const [name, setName] = useState<string>('');
	const [nameError, setNameError] = useState<string>('');
	const [categoryId, setCategoryId] = useState<number>(0);
	const [categoryError, setCategoryError] = useState<string>('');
	const [goalName, setGoalName] = useState<string>('');
	const [definition, setDefinition] = useState<string>('');
	const [recordingType, setRecordingType] = useState<number>(0);
	const [recordingTypeError, setRecordingTypeError] = useState<string>('');
	const [typesData, setTypesData] = useState<SelectDataProperties[]>([]);

	const loading = useSelector((state) => state.behaviorReduction.loading);
	const categories = useSelector((state) => state.behaviorReduction.behaviorCategories);
	const types = useSelector((state) => state.behaviorReduction.behaviorTypes);

	useEffect(() => {
		dispatch(getBehaviorCategories());
		dispatch(getBehaviorTypes());
	}, [dispatch]);

	useEffect(() => {
		if (Boolean(typesData.length) || !types.length) return;
		const value = types.map((t) => ({ id: t.id, name: t.text }));
		setTypesData(value);
	}, [typesData, types]);

	const onNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setName(value);
		setNameError('');
	};

	const onNameBlur = () => {
		if (name) return;
		setNameError('Required');
	};

	const onGoalNameChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setGoalName(value);
	};

	const onDefinitionChange = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setDefinition(value);
	};

	const onCategoryIdChange = (event: SelectChangeEvent<string>) => {
		const value = parseInt(event.target.value);
		setCategoryId(value);
		setCategoryError('');
	};

	const onCategoryBlur = () => {
		if (categoryId) return;
		setCategoryError('Required');
	};

	const onTypeIdChange = (event: SelectChangeEvent<string>) => {
		const value = parseInt(event.target.value);
		setRecordingType(value);
		setRecordingTypeError('');
	};

	const onRecordingTypeBlur = () => {
		if (recordingType) return;
		setRecordingTypeError('Required');
	};

	const addBehaviorHandler = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!name) setNameError('Required');
		if (!recordingType) setRecordingTypeError('Required');
		if (!categoryId) setCategoryError('Required');

		if (!clientId || !name || !recordingType || !categoryId) return;
		const includeArchived = params.get('includeArchivedBehavior') === 'true';
		const data: SaveClientBehaviorsProperties = {
			clientId,
			funderId: funderId ?? undefined,
			name,
			categoryId,
			goalName,
			definition,
			recordingType,
			doNotZeroOut: false,
		};

		dispatch(saveBehavior(data))
			.then(unwrapResult)
			.then(() => {
				dispatch(getBehaviors({ clientId, includeArchived, funderId }));
				onClose();
			})
			.catch(errorNormalizer);
	};

	return (
		<form onSubmit={addBehaviorHandler}>
			<CustomInput
				label="Name"
				value={name}
				onChange={onNameChange}
				className="marginBottom8"
				onBlur={onNameBlur}
				error={Boolean(nameError)}
				errorMessage={nameError}
				backgroundType="base"
			/>
			<CustomSelect
				label="Category"
				value={`${categoryId}`}
				data={categories}
				setValue={onCategoryIdChange}
				className="marginBottom8"
				onBlur={onCategoryBlur}
				error={Boolean(categoryError)}
				errorMessage={categoryError}
			/>
			<CustomInput
				label="Goal Name"
				value={goalName}
				onChange={onGoalNameChange}
				className="marginBottom8"
				backgroundType="base"
			/>
			<CustomInput
				label="Definition"
				value={definition}
				onChange={onDefinitionChange}
				className="marginBottom8"
				multiline
				backgroundType="base"
			/>
			<CustomSelect
				label="Recording type"
				value={`${recordingType}`}
				setValue={onTypeIdChange}
				data={typesData}
				className="marginBottom16"
				onBlur={onRecordingTypeBlur}
				error={Boolean(recordingTypeError)}
				errorMessage={recordingTypeError}
			/>
			<CustomButton
				title="Save"
				type="submit"
				disabled={!clientId || Boolean(nameError) || Boolean(recordingTypeError) || Boolean(categoryError)}
				loading={loading}
			/>
		</form>
	);
};

const AddBehavior = ({ funderId, mobileButton }: AddBehaviorProps = {}) => {
	const [open, setOpen] = useState<boolean>(false);
	const screenWidth = useScreenWidth();
	const isMobile = screenWidth <= 768;

	const closeHandler = () => setOpen(false);
	const openHandler = () => setOpen(true);
	const role = useSelector((state) => state.account.role);

	if (role && role.section.id === AdminTypes.bt) return null;

	return (
		<div>
			<CustomButton
				title={mobileButton ? '' : isMobile ? '' : 'Create new behavior'}
				iconOnly={mobileButton ? <PlusIcon /> : undefined}
				onClick={openHandler}
				startIcon={mobileButton ? undefined : <Plus color={BACKGROUND.PRIMARY[100]} />}
				buttonType="primary"
				sizeType={mobileButton ? 'sm' : undefined}
			/>
			<CustomAlert
				title={funderId?.toLowerCase() === ELC_FUNDER_ID ? 'Add new behavior (ELC)' : 'Add new behavior'}
				open={open}
				onClose={closeHandler}
				Content={({ onClose }) => <AddBehaviorContent onClose={onClose} funderId={funderId} />}
			/>
		</div>
	);
};

export default AddBehavior;
